import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Users, MapPin, Globe } from "lucide-react";

const TOHU = "/tiina-logo-transparent.png";

const config = {
  people: {
    icon: Users,
    label: "People",
    tagline: "The heart of everything",
    description: "Health, workforce, education, whānau wellbeing, cultural identity, and economic participation for Māori and Pasifika communities across Aotearoa.",
    bg: "",
    bgStyle: { background: "#141210" },
    headerBg: "linear-gradient(135deg, #141210, #45443f, #141210)",
    contentBg: "#141210",
    accentBg: "rgba(204,85,0,0.08)",
    textColour: "#bb321a",
    borderColour: "#bb321a",
    dark: true,
    glowColour: "rgba(204,85,0,0.12)",
  },
  place: {
    icon: MapPin,
    label: "Place",
    tagline: "Identity, memory, and knowledge",
    description: "Regional economic development, iwi enterprise, infrastructure, investment, and the activation of Indigenous land and resource assets.",
    bg: "",
    bgStyle: { background: "#121714" },
    headerBg: "linear-gradient(135deg, #121714, #1A1F1B, #121714)",
    contentBg: "#121714",
    accentBg: "rgba(4,78,59,0.08)",
    textColour: "#10B981",
    borderColour: "#364225",
    dark: true,
    glowColour: "rgba(4,78,59,0.15)",
  },
  planet: {
    icon: Globe,
    label: "Planet",
    tagline: "Systemic thinking, long-term vision",
    description: "Climate adaptation, kaitiakitanga, conservation, biodiversity, environmental governance, and the protection of taonga species and whenua.",
    bg: "",
    bgStyle: { background: "#101218" },
    headerBg: "linear-gradient(135deg, #101218, #161A22, #101218)",
    contentBg: "#101218",
    accentBg: "rgba(59,130,180,0.06)",
    textColour: "#bb321a",
    borderColour: "#3B82AA",
    dark: true,
    glowColour: "rgba(4,78,59,0.10)",
  },
};

export default function DomainWorld({ domain, children }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const c = config[domain];
  if (!c) return children;
  const Icon = c.icon;

  return (
    <div
      id={domain}
      ref={ref}
      data-testid={`domain-world-${domain}`}
      className={`relative overflow-hidden ${c.bg}`}
      style={c.bgStyle || {}}
    >
      {/* Subtle Tohu watermark for this world */}
      <div className="absolute top-20 right-0 w-[400px] h-[400px] sm:w-[500px] sm:h-[500px] pointer-events-none select-none" style={{ opacity: 0.03 }}>
        <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: c.accentBg }}>
          <img src={TOHU} alt="" className="w-[140%] h-[140%] object-cover" style={{ filter: "invert(1)" }} />
        </div>
      </div>

      {/* Ambient colour glow - top left */}
      <div className="absolute -top-40 -left-40 w-[800px] h-[800px] rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${c.glowColour || 'transparent'}, transparent 60%)`, filter: "blur(100px)" }} />
      {/* Ambient colour glow - bottom right */}
      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${c.glowColour || 'transparent'}, transparent 60%)`, filter: "blur(80px)" }} />
      {/* Accent line at top of each world */}
      <div className="w-full h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${c.textColour}40, transparent)` }} />

      {/* PROMINENT DOMAIN HEADER - full width, bold, unmissable */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
        className="relative overflow-hidden"
        style={{ background: c.headerBg }}
      >
        {/* Large background text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span className="font-['Syne'] text-[180px] sm:text-[250px] lg:text-[350px] font-black uppercase text-white/[0.04] leading-none tracking-tighter">
            {c.label}
          </span>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14 lg:py-16 relative z-10">
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${c.textColour}15`, border: `2px solid ${c.textColour}30` }}>
              <Icon className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 ${domain === "planet" ? "animate-spin-slow" : ""}`} style={{ color: c.textColour }} strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="font-['Syne'] text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight">
                {c.label}<span style={{ color: c.textColour }}>.</span>
              </h2>
              <p className="text-base sm:text-lg text-white/70 mt-2 font-light">
                {c.tagline}
              </p>
              <p className="text-sm text-white/50 mt-1 max-w-xl leading-relaxed hidden sm:block">
                {c.description}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom edge gradient fade into content area */}
        <div className="h-4" style={{ background: `linear-gradient(180deg, transparent, ${c.contentBg})` }} />
      </motion.div>

      {/* Content flows within this world */}
      <div className="relative z-10">
        {children}
      </div>

      <div className="h-4" />
    </div>
  );
}
